import { ArrowUp, Copy, GitBranch, Globe2, History, Plus, Search, UploadCloud, X } from "lucide-react";
import {
  FocusQueryDecisionSchema,
  type CapabilityManifest,
  type FocusMessageProjection,
  type FocusQueryCandidate,
  type FocusRunProjection,
  type FocusRunState,
  type FocusThreadProjection,
  type ProposalProjection,
} from "@mind/contracts/browser";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useCallback, useEffect, useId, useMemo, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useTranslation } from "react-i18next";
import { ApiError } from "../../api/client.js";
import { useAppSession } from "../../app/AppSession.js";
import { documentApi } from "../documents/documentApi.js";
import { UploadQueue } from "../documents/DocumentsScreen.js";
import { useDocumentUploadQueue, type DocumentUploadQueueEntry } from "../documents/useDocumentUploadQueue.js";
import { documentsQueryKeys } from "../documents/queryKeys.js";
import { focusApi } from "./focusApi.js";
import { copyFocusText, focusDraftKey, seedPartialStartDraft, selectFocusStarter, shouldShowPendingPrompt, writeFocusDraft, proposalOperationKey as getProposalOperationKey } from "./focusState.js";
import { buildContextChoices, type ContextChoice } from "./focusContext.js";
import { createCapabilityManifest, manifestRegistrationExpiry } from "./manifest.js";
import { focusQueryKeys } from "./queryKeys.js";
import { RunEventPanel, isTerminalFocusState } from "./RunEventPanel.js";
import {
  activeFocusSearch,
  newFocusSearch,
  validateFocusSearch,
  withHistoryState,
} from "./searchState.js";
import {
  boundedPreviousMessageCursor,
  initialMessageCursor,
  isPinnedToEnd,
  preservedScrollTop,
} from "./scroll.js";
import { withGraphRoot } from "../graph/searchState.js";
import styles from "./FocusScreen.module.css";

const HISTORY_PAGE_SIZE = 30;
const MESSAGE_PAGE_SIZE = 100;
const MAX_CONTEXT_CHOICES = 50;

type StartedRun = {
  readonly run: FocusRunProjection;
  readonly threadId: string;
  readonly branchId: string;
};
type PartialStart = {
  readonly threadId: string;
  readonly branchId: string;
  readonly prompt: string;
};

type PendingPrompt = PartialStart & {
  readonly runId: string;
  readonly prompt: string;
};

type FocusQueryDecisionInput =
  | { readonly decision: "approve" }
  | { readonly decision: "edit"; readonly queries: readonly string[] }
  | { readonly decision: "reject" };

export function FocusScreen() {
  const { t } = useTranslation();
  const session = useAppSession();
  const navigate = useNavigate();
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const queryClient = useQueryClient();
  const rawSearch = useRouterState({ select: (state) => state.location.search });
  const search = validateFocusSearch(rawSearch);
  const mode = search.mode ?? (search.thread === undefined ? "new" : "active");
  const workspace = session.workspaces.find((candidate) => candidate.id === search.workspace)
    ?? session.selectedWorkspace;
  const identity = session.identity?.effective ?? null;

  const [historyInput, setHistoryInput] = useState("");
  const [historyQuery, setHistoryQuery] = useState("");
  const [desktopHistoryCollapsed, setDesktopHistoryCollapsed] = useState(true);
  const [publicSearchEnabled, setPublicSearchEnabled] = useState(false);
  const [selectedContextKeys, setSelectedContextKeys] = useState<ReadonlySet<string>>(new Set());
  const [activeRunReference, setActiveRunReference] = useState<StartedRun | null>(null);
  const [pendingPrompt, setPendingPrompt] = useState<PendingPrompt | null>(null);
  const [partialStart, setPartialStart] = useState<PartialStart | null>(null);
  const partialStartRef = useRef<PartialStart | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const [showJumpLatest, setShowJumpLatest] = useState(false);
  const messageScrollerRef = useRef<HTMLDivElement>(null);
  const historyDialogRef = useRef<HTMLDialogElement>(null);
  const focusTitleRef = useRef<HTMLHeadingElement>(null);
  const mobileHistoryButtonRef = useRef<HTMLButtonElement>(null);
  const historyOpenedFromButtonRef = useRef(false);
  const pinnedRef = useRef(true);
  const announcedRunStateRef = useRef<string | null>(null);

  const workspaceId = workspace?.id ?? "";
  const history = useInfiniteQuery({
    queryKey: focusQueryKeys.threads(workspaceId, historyQuery),
    enabled: workspace !== null,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) => historyQuery.length === 0
      ? focusApi.listThreads(workspaceId, pageParam, HISTORY_PAGE_SIZE, signal)
      : focusApi.searchThreads(workspaceId, historyQuery, pageParam, HISTORY_PAGE_SIZE, signal),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    maxPages: 3,
  });
  const historyItems = useMemo(
    () => history.data?.pages.flatMap((page) => page.items) ?? [],
    [history.data],
  );

  const threadId = mode === "active" ? search.thread ?? null : null;
  const draftKey = focusDraftKey(workspaceId, mode, threadId);
  const draft = drafts[draftKey] ?? "";
  const setDraft = (value: string): void => {
    setDrafts((current) => writeFocusDraft(current, draftKey, value));
  };
  const thread = useQuery({
    queryKey: focusQueryKeys.thread(threadId ?? "none"),
    enabled: threadId !== null,
    queryFn: ({ signal }) => focusApi.getThread(threadId ?? "", signal),
  });
  const selectedBranch = thread.data?.branches.find((branch) => branch.id === search.branch)
    ?? thread.data?.branches[0]
    ?? null;
  const messages = useInfiniteQuery({
    queryKey: focusQueryKeys.messages(
      threadId ?? "none",
      selectedBranch?.id ?? "none",
      selectedBranch?.headSequence ?? "0",
    ),
    enabled: threadId !== null && selectedBranch !== null,
    initialPageParam: initialMessageCursor(selectedBranch?.headSequence ?? "0"),
    queryFn: ({ pageParam, signal }) => focusApi.getMessages(
      threadId ?? "",
      selectedBranch?.id ?? "",
      pageParam,
      MESSAGE_PAGE_SIZE,
      signal,
    ),
    getNextPageParam: () => undefined,
    getPreviousPageParam: (_firstPage, allPages, firstPageParam) => (
      boundedPreviousMessageCursor(firstPageParam, allPages.length)
    ),
  });
  const messageItems = useMemo(() => {
    const unique = new Map<string, FocusMessageProjection>();
    for (const page of messages.data?.pages ?? []) {
      for (const message of page.items) unique.set(message.id, message);
    }
    return Array.from(unique.values()).sort((left, right) => {
      const leftSequence = BigInt(left.sequence);
      const rightSequence = BigInt(right.sequence);
      return leftSequence < rightSequence ? -1 : leftSequence > rightSequence ? 1 : 0;
    });
  }, [messages.data]);

  const contextDocuments = useQuery({
    queryKey: documentsQueryKeys.list(workspaceId),
    enabled: workspace !== null,
    queryFn: ({ signal }) => documentApi.list(workspaceId, signal),
    staleTime: 30_000,
  });
  const contextTasks = useQuery({
    queryKey: focusQueryKeys.contextTasks(workspaceId),
    enabled: workspace !== null,
    queryFn: ({ signal }) => focusApi.getContextTasks(workspaceId, signal),
    staleTime: 30_000,
  });
  const handleUploaded = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: documentsQueryKeys.all });
  }, [queryClient]);
  const uploadQueue = useDocumentUploadQueue(workspaceId, handleUploaded);
  const contextChoices = useMemo(
    () => buildContextChoices(
      contextDocuments.data?.items ?? [],
      contextTasks.data?.items ?? [],
      messageItems,
      t("focus.contextMessage"),
      {
        uploading: t("focus.contextReasons.uploading"),
        security: t("focus.contextReasons.security"),
        unsafe: t("focus.contextReasons.unsafe"),
        unsupported: t("focus.contextReasons.unsupported"),
        blocked: t("focus.contextReasons.blocked"),
      },
    ),
    [contextDocuments.data, contextTasks.data, messageItems, t],
  );
  const selectedContextItems = useMemo(
    () => contextChoices
      .filter((choice) => selectedContextKeys.has(choice.key))
      .map(({ key: _key, label: _label, detail: _detail, disabledReason: _disabledReason, ...item }) => item),
    [contextChoices, selectedContextKeys],
  );

  const latestMessage = messageItems.at(-1) ?? null;
  const referencedRunId = activeRunReference !== null
      && activeRunReference.threadId === threadId
      && activeRunReference.branchId === selectedBranch?.id
    ? activeRunReference.run.id
    : null;
  const runId = referencedRunId ?? latestMessage?.runId ?? null;
  const runDetail = useQuery({
    queryKey: focusQueryKeys.run(runId ?? "none"),
    enabled: runId !== null,
    queryFn: ({ signal }) => focusApi.getRun(runId ?? "", signal),
  });
  const run = runDetail.data?.run ?? null;
  const activeTailIsAuthoritative = mode === "new"
    || (messages.isSuccess && (
      runId === null
        ? latestMessage === null
        : runDetail.isSuccess && run !== null && isTerminalFocusState(run.state)
    ));
  const hasPersistedAssistant = runId !== null
    && messageItems.some((message) => message.runId === runId && message.role === "assistant");
  const proposalId = run?.proposalId ?? null;
  const proposal = useQuery({
    queryKey: focusQueryKeys.proposal(proposalId ?? "none"),
    enabled: proposalId !== null,
    queryFn: ({ signal }) => focusApi.getProposal(proposalId ?? "", signal),
  });

  useEffect(() => {
    setSelectedContextKeys(new Set());
  }, [workspaceId, identity?.id, threadId, selectedBranch?.id]);
  useEffect(() => {
    if (
      pendingPrompt === null
      || pendingPrompt.threadId !== threadId
      || pendingPrompt.branchId !== selectedBranch?.id
    ) return;
    if (messageItems.some((message) => message.runId === pendingPrompt.runId && message.role === "user")) {
      setPendingPrompt(null);
    }
  }, [messageItems, pendingPrompt, selectedBranch?.id, threadId]);

  useEffect(() => {
    setSelectedContextKeys((current) => {
      if (current.size === 0) return current;
      const available = new Set(contextChoices.filter((choice) => choice.disabledReason === null).map((choice) => choice.key));
      const next = new Set(Array.from(current).filter((key) => available.has(key)));
      return next.size === current.size ? current : next;
    });
  }, [contextChoices]);
  useEffect(() => {
    const eligibleUploadKeys = new Set(
      contextChoices
        .filter((choice) => choice.kind === "document" && choice.disabledReason === null)
        .map((choice) => choice.key),
    );
    const autoSelectKeys = uploadQueue.entries
      .map((entry) => entry.result?.document.id === undefined ? null : `document:${entry.result.document.id}`)
      .filter((key): key is string => key !== null && eligibleUploadKeys.has(key));
    if (autoSelectKeys.length === 0) return;
    setSelectedContextKeys((current) => {
      const next = new Set(current);
      for (const key of autoSelectKeys) {
        if (next.size >= MAX_CONTEXT_CHOICES) break;
        next.add(key);
      }
      return next.size === current.size ? current : next;
    });
  }, [contextChoices, uploadQueue.entries]);

  useEffect(() => {
    const dialog = historyDialogRef.current;
    if (dialog === null) return;
    if (search.history === "open" && !dialog.open) dialog.showModal();
    if (search.history !== "open" && dialog.open) dialog.close();
  }, [search.history]);

  useEffect(() => {
    if (!pinnedRef.current) return;
    const frame = window.requestAnimationFrame(() => {
      const scroller = messageScrollerRef.current;
      if (scroller !== null) scroller.scrollTop = scroller.scrollHeight;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [messageItems.length]);

  useEffect(() => {
    pinnedRef.current = true;
    setShowJumpLatest(false);
    const frame = window.requestAnimationFrame(() => {
      const scroller = messageScrollerRef.current;
      if (scroller !== null) scroller.scrollTop = scroller.scrollHeight;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [threadId, selectedBranch?.id, runId]);

  useEffect(() => {
    if (run === null || (run.state !== "awaiting_query_review" && !isTerminalFocusState(run.state))) return;
    const announcementKey = `${run.id}:${run.state}`;
    if (announcedRunStateRef.current === announcementKey) return;
    announcedRunStateRef.current = announcementKey;
    setAnnouncement(run.state === "awaiting_query_review"
      ? t("focus.announceQueryReview")
      : t("focus.announceTerminal", { state: t(`focus.states.${run.state}`) }));
  }, [run, t]);

  const registerManifest = async (attemptId: string): Promise<CapabilityManifest> => {
    if (workspace === null || identity === null) throw new Error("Focus authority is unavailable");
    const manifest = await createCapabilityManifest({
      manifestId: crypto.randomUUID(),
      attemptId,
      effectiveAccountId: identity.id,
      workspaceId: workspace.id,
      workspaceRole: workspace.currentRole,
      contextItems: selectedContextItems,
    });
    return focusApi.registerManifest(workspace.id, manifest, manifestRegistrationExpiry());
  };

  const startRun = useMutation({
    mutationFn: async (): Promise<StartedRun> => {
      if (workspace === null || identity === null) throw new Error("Focus authority is unavailable");
      const prompt = draft.trim();
      if (prompt.length === 0) throw new Error("Focus prompt is empty");

      let targetThreadId = threadId;
      let targetBranchId = selectedBranch?.id ?? null;
      let baseThreadSequence = selectedBranch?.headSequence ?? "0";
      let parentMessageId = latestMessage?.id ?? null;
      if (mode === "new") {
        targetThreadId = crypto.randomUUID();
        targetBranchId = crypto.randomUUID();
        const firstLine = prompt.split(/\r?\n/u).find((line) => line.trim().length > 0) ?? t("focus.untitled");
        const created = await focusApi.createThread({
          commandId: crypto.randomUUID(),
          threadId: targetThreadId,
          branchId: targetBranchId,
          workspaceId: workspace.id,
          title: firstLine.trim().slice(0, 200),
          branchName: "main",
        });
        baseThreadSequence = created.branch.headSequence;
        parentMessageId = null;
        const partial = { threadId: targetThreadId, branchId: targetBranchId, prompt };
        partialStartRef.current = partial;
        setPartialStart(partial);
      }
      if (targetThreadId === null || targetBranchId === null) throw new Error("Focus thread is unavailable");

      const commandRunId = crypto.randomUUID();
      const manifest = await registerManifest(commandRunId);
      const started = await focusApi.submitRun({
        commandId: crypto.randomUUID(),
        runId: commandRunId,
        threadId: targetThreadId,
        branchId: targetBranchId,
        parentMessageId,
        baseThreadSequence,
        uiLocale: identity.uiLocale,
        assistantLocale: identity.assistantLocale,
        prompt,
        contextManifestId: manifest.manifestId,
        contextManifestDigest: manifest.digest,
        explicitPublicQuery: publicSearchEnabled ? prompt : null,
      });
      return { run: started, threadId: targetThreadId, branchId: targetBranchId };
    },
  });

  const queryDecision = useMutation({
    mutationFn: async (decision: FocusQueryDecisionInput) => {
      const candidate = runDetail.data?.queryCandidate;
      if (runId === null || candidate === null || candidate === undefined) throw new Error("Query unavailable");
      const body = FocusQueryDecisionSchema.parse({
        commandId: crypto.randomUUID(),
        runId,
        decision: decision.decision,
        candidateRevision: candidate.candidateRevision,
        ...(decision.decision === "reject" ? {} : { manifestDigest: candidate.manifestDigest }),
        ...(decision.decision === "edit" ? { queries: decision.queries } : {}),
      });
      return focusApi.decideQuery(runId, body);
    },
    onSuccess: async () => {
      if (runId !== null) await queryClient.invalidateQueries({ queryKey: focusQueryKeys.run(runId) });
    },
  });

  const cancelRun = useMutation({
    mutationFn: async () => {
      if (runId === null) throw new Error("Run unavailable");
      return focusApi.cancelRun(runId);
    },
    onMutate: () => setAnnouncement(t("focus.announceCancelling")),
    onSuccess: async () => {
      if (runId !== null) await queryClient.invalidateQueries({ queryKey: focusQueryKeys.run(runId) });
    },
  });

  const retryRun = useMutation({
    mutationFn: async (): Promise<StartedRun> => {
      if (runId === null || selectedBranch === null || latestMessage === null) throw new Error("Retry unavailable");
      const retryRunId = crypto.randomUUID();
      const manifest = await registerManifest(retryRunId);
      const retried = await focusApi.retryRun(runId, {
        commandId: crypto.randomUUID(),
        retryRunId,
        parentMessageId: latestMessage.id,
        baseThreadSequence: selectedBranch.headSequence,
        contextManifestId: manifest.manifestId,
        contextManifestDigest: manifest.digest,
      });
      return { run: retried, threadId: retried.threadId, branchId: retried.branchId };
    },
  });

  const decideProposal = useMutation({
    mutationFn: async (action: "accept" | "reject") => {
      if (proposal.data === undefined) throw new Error("Proposal unavailable");
      return focusApi.decideProposal(proposal.data, action);
    },
    onSuccess: (updated) => queryClient.setQueryData(focusQueryKeys.proposal(updated.id), updated),
  });

  const openThread = async (candidate: FocusThreadProjection): Promise<void> => {
    await navigate({ to: "/focus", search: activeFocusSearch(search, candidate.id, undefined, null) });
  };

  const completeStartedRun = async (started: StartedRun, prompt: string): Promise<void> => {
    partialStartRef.current = null;
    setPartialStart(null);
    setActiveRunReference(started);
    setPendingPrompt({ threadId: started.threadId, branchId: started.branchId, runId: started.run.id, prompt });
    setDraft("");
    setSelectedContextKeys(new Set());
    await queryClient.invalidateQueries({ queryKey: focusQueryKeys.threads(workspaceId, "") });
    await navigate({
      to: "/focus",
      search: activeFocusSearch(search, started.threadId, started.branchId, null),
    });
  };

  const submitPrompt = async (): Promise<void> => {
    const prompt = draft.trim();
    if (mode === "new") {
      partialStartRef.current = null;
      setPartialStart(null);
    }
    try {
      setAnnouncement(t("focus.announceQueued"));
      await completeStartedRun(await startRun.mutateAsync(), prompt);
    } catch (error) {
      const partial = partialStartRef.current;
      if (error instanceof ApiError && error.code === "STALE_REVISION") {
        setAnnouncement(t("focus.announceConflict"));
      } else if (partial !== null) {
        setDrafts((current) => seedPartialStartDraft(current, workspaceId, partial));
        setAnnouncement(t("focus.partialStartFailed"));
      } else {
        setAnnouncement(t("focus.startFailed"));
      }
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    await submitPrompt();
  };

  const handleRetry = async (): Promise<void> => {
    try {
      setAnnouncement(t("focus.announceQueued"));
      setActiveRunReference(await retryRun.mutateAsync());
    } catch (error) {
      if (error instanceof ApiError && error.code === "STALE_REVISION") setAnnouncement(t("focus.announceConflict"));
    }
  };

  const handleCopy = async (text: string): Promise<void> => {
    const writeText = navigator.clipboard?.writeText.bind(navigator.clipboard);
    const result = writeText === undefined ? "failed" : await copyFocusText(text, writeText);
    setAnnouncement(result === "copied" ? t("focus.copied") : t("focus.copyFailed"));
  };
  const handleOlderMessages = async (): Promise<void> => {
    const scroller = messageScrollerRef.current;
    if (scroller === null) return;
    const previousHeight = scroller.scrollHeight;
    const previousTop = scroller.scrollTop;
    pinnedRef.current = false;
    await messages.fetchPreviousPage();
    window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
      const current = messageScrollerRef.current;
      if (current !== null) current.scrollTop = preservedScrollTop(previousTop, previousHeight, current.scrollHeight);
    }));
  };

  const followLatest = (): void => {
    pinnedRef.current = true;
    setShowJumpLatest(false);
    const scroller = messageScrollerRef.current;
    if (scroller !== null) scroller.scrollTop = scroller.scrollHeight;
  };

  const handleRunBoundary = (state: FocusRunState): void => {
    if (runId === null || (state !== "awaiting_query_review" && !isTerminalFocusState(state))) return;
    if (state === "awaiting_query_review") {
      void queryClient.invalidateQueries({ queryKey: focusQueryKeys.run(runId) });
    }
    const announcementKey = `${runId}:${state}`;
    if (announcedRunStateRef.current === announcementKey) return;
    announcedRunStateRef.current = announcementKey;
    setAnnouncement(state === "awaiting_query_review"
      ? t("focus.announceQueryReview")
      : t("focus.announceTerminal", { state: t(`focus.states.${state}`) }));
  };

  const handleRunTerminal = (): void => {
    if (runId === null || threadId === null || selectedBranch === null) return;
    void queryClient.invalidateQueries({ queryKey: focusQueryKeys.run(runId) });
    void queryClient.invalidateQueries({ queryKey: focusQueryKeys.thread(threadId) });
    void queryClient.invalidateQueries({
      predicate: (query) => query.queryKey[0] === "focus"
        && query.queryKey[1] === "messages"
        && query.queryKey[2] === threadId
        && query.queryKey[3] === selectedBranch.id,
    });
  };

  const handleStreamContent = (): void => {
    if (!pinnedRef.current) return;
    window.requestAnimationFrame(() => {
      const scroller = messageScrollerRef.current;
      if (scroller !== null) scroller.scrollTop = scroller.scrollHeight;
    });
  };
  const toggleContext = (key: string): void => {
    const choice = contextChoices.find((candidate) => candidate.key === key);
    if (choice === undefined || choice.disabledReason !== null) return;
    setSelectedContextKeys((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else if (next.size < MAX_CONTEXT_CHOICES) next.add(key);
      return next;
    });
  };
  const uploadBlocksSubmit = uploadQueue.entries.some((entry) => {
    if (entry.phase === "queued" || entry.phase === "hashing" || entry.phase === "uploading") return true;
    const result = entry.result;
    if (result === null) return false;
    return !contextChoices.some((choice) => choice.key === `document:${result.document.id}` && choice.disabledReason === null);
  });

  const activeThreadReady = mode === "new"
    || (thread.data !== undefined && selectedBranch !== null && messages.isSuccess && messages.error === null);
  const activeThreadLoading = mode === "active"
    && (thread.isPending || (thread.data !== undefined && (selectedBranch === null || messages.isPending)) || (runId !== null && runDetail.isPending));
  const activeThreadError = mode === "active" && (thread.error !== null || messages.error !== null);
  const threadNotFound = mode === "active" && thread.error instanceof ApiError && thread.error.status === 404;
  const submitDisabled = workspace === null
    || identity === null
    || draft.trim().length === 0
    || startRun.isPending
    || !activeThreadReady
    || !activeTailIsAuthoritative
    || uploadBlocksSubmit;
  const composerDisabledReason = mode === "active" && activeThreadError
    ? threadNotFound ? t("focus.threadNotFoundDescription") : thread.error !== null ? t("focus.threadErrorDescription") : t("focus.messagesError")
    : uploadBlocksSubmit
      ? t("focus.uploadPending")
      : mode === "active" && !activeThreadReady
        ? t("focus.threadLoading")
        : null;
  const mutationError = startRun.error ?? queryDecision.error ?? cancelRun.error ?? retryRun.error ?? decideProposal.error;

  if (workspace === null || identity === null) {
    return <section className={styles.unavailable} aria-labelledby="focus-title"><h1 id="focus-title">{t("focus.title")}</h1><p>{t("focus.noWorkspace")}</p></section>;
  }

  const retryContextSources = (): void => {
    if (contextDocuments.isError) void contextDocuments.refetch();
    if (contextTasks.isError) void contextTasks.refetch();
  };
  const historyPanel = (
    <HistoryPanel
      input={historyInput}
      query={historyQuery}
      items={historyItems}
      pending={history.isFetching}
      error={history.isError}
      hasMore={history.hasNextPage}
      onInput={setHistoryInput}
      onSearch={() => setHistoryQuery(historyInput.trim())}
      onClear={() => { setHistoryInput(""); setHistoryQuery(""); }}
      onMore={() => void history.fetchNextPage()}
      onRetry={() => void history.refetch()}
      onOpen={openThread}
    />
  );

  const handleHistoryDialogClose = (): void => {
    const restoreToButton = historyOpenedFromButtonRef.current;
    historyOpenedFromButtonRef.current = false;
    if (search.history === "open") {
      void navigate({ to: "/focus", search: withHistoryState(search, false), replace: true });
    }
    const mobileButton = mobileHistoryButtonRef.current;
    const buttonVisible = mobileButton !== null && window.getComputedStyle(mobileButton).display !== "none";
    (restoreToButton && buttonVisible ? mobileButton : focusTitleRef.current)?.focus();
  };

  return (
    <section className={styles.focus} aria-labelledby="focus-title">
      <p className={styles.liveRegion} aria-live="polite" aria-atomic="true">{announcement}</p>
      <header className={styles.header}>
        <div>
          <p className={styles.eyebrow}>{workspace.name}</p>
          <h1 ref={focusTitleRef} id="focus-title" tabIndex={-1}>{mode === "new" ? t("focus.title") : thread.data?.thread.title ?? t("focus.title")}</h1>
        </div>
        <div className={styles.headerActions}>
          <button
            ref={mobileHistoryButtonRef}
            type="button"
            className={styles.mobileHistoryButton}
            onClick={() => {
              historyOpenedFromButtonRef.current = true;
              void navigate({ to: "/focus", search: withHistoryState(search, true) });
            }}
          >
            <History aria-hidden="true" size={17} />
            {t("focus.historyAction")}
          </button>
          <button
            type="button"
            className={styles.historyCollapseButton}
            aria-expanded={!desktopHistoryCollapsed}
            aria-controls="focus-desktop-history"
            onClick={() => setDesktopHistoryCollapsed((current) => !current)}
          >
            <History aria-hidden="true" size={17} />
            {t("focus.historyAction")}
          </button>
          {mode === "active" ? (
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={() => void navigate({ to: "/focus", search: newFocusSearch(search, null) })}
            >
              <Plus aria-hidden="true" size={17} />
              {t("focus.newThreadAction")}
            </button>
          ) : null}
          {mode === "active" && thread.data !== undefined ? (
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={() => void navigate({ to: "/graph", search: withGraphRoot({ workspace: workspaceId }, { type: "focus_thread", id: thread.data.thread.id, revision: thread.data.thread.revision }) })}
            >
              <GitBranch aria-hidden="true" size={17} />
              {t("focus.showConnections")}
            </button>
          ) : null}
        </div>
      </header>

      <div className={styles.layout} data-history-collapsed={desktopHistoryCollapsed}>
        <aside
          id="focus-desktop-history"
          className={styles.desktopHistory}
          aria-label={t("focus.history")}
          hidden={desktopHistoryCollapsed}
        >
          {historyPanel}
        </aside>
        <main className={styles.threadSurface} aria-busy={activeThreadLoading}>
          {mode === "new" ? (
            <div className={styles.newThread}>
              <div className={styles.newThreadIntro}>
                <h2>{t("focus.emptyHeading")}</h2>
                <p>{t("focus.emptyDescription")}</p>
                <div className={styles.starterActions} aria-labelledby="focus-starters-label">
                  <span id="focus-starters-label" className={styles.eyebrow}>{t("focus.suggestionsLabel")}</span>
                  <div className={styles.starterButtons}>
                    <button type="button" onClick={() => setDraft(selectFocusStarter(t("focus.suggestions.plan")).draft)}>{t("focus.suggestions.plan")}</button>
                    <button type="button" onClick={() => setDraft(selectFocusStarter(t("focus.suggestions.compare")).draft)}>{t("focus.suggestions.compare")}</button>
                    <button type="button" onClick={() => setDraft(selectFocusStarter(t("focus.suggestions.summarize")).draft)}>{t("focus.suggestions.summarize")}</button>
                  </div>
                </div>
              </div>
              <Composer
                draft={draft}
                publicSearchEnabled={publicSearchEnabled}
                contextChoices={contextChoices}
                uploadEntries={uploadQueue.entries}
                onUploadFiles={(files) => uploadQueue.enqueue(files)}
                onRetryUpload={uploadQueue.retry}
                onCancelUpload={uploadQueue.cancel}
                onRemoveUpload={uploadQueue.remove}
                onOpenDocuments={() => void navigate({ to: "/documents", search: { workspace: workspaceId } })}
                contextLoading={contextDocuments.isPending || contextTasks.isPending}
                contextUnavailable={contextDocuments.isError || contextTasks.isError}
                contextRetrying={(contextDocuments.isError && contextDocuments.isFetching) || (contextTasks.isError && contextTasks.isFetching)}
                selectedContextKeys={selectedContextKeys}
                disabled={submitDisabled}
                pending={startRun.isPending}
                disabledReason={composerDisabledReason}
                onDraft={setDraft}
                onPublicSearchEnabled={setPublicSearchEnabled}
                onToggleContext={toggleContext}
                onRetryContext={retryContextSources}
                onSubmit={handleSubmit}
              />
            </div>
          ) : (
            <>
              {thread.data !== undefined && thread.data.branches.length > 1 ? (
                <label className={styles.branchSelector}><span>{t("focus.branch")}</span><select value={selectedBranch?.id ?? ""} onChange={(event) => void navigate({ to: "/focus", search: activeFocusSearch(search, thread.data.thread.id, event.currentTarget.value, null) })}>{thread.data.branches.map((branch) => <option key={branch.id} value={branch.id}>{branch.name}</option>)}</select></label>
              ) : null}
              {thread.isPending ? <p className={styles.inlineState} role="status">{t("focus.threadLoading")}</p> : null}
              {thread.error !== null && !threadNotFound ? (
                <div className={styles.inlineState} role="alert">
                  <strong>{t("focus.threadError")}</strong>
                  <p>{t("focus.threadErrorDescription")}</p>
                  <button type="button" onClick={() => void thread.refetch()}>{t("focus.retryThread")}</button>
                </div>
              ) : null}
              {threadNotFound ? (
                <div className={styles.inlineState} role="alert">
                  <strong>{t("focus.threadNotFound")}</strong>
                  <p>{t("focus.threadNotFoundDescription")}</p>
                  <button type="button" onClick={() => void navigate({ to: "/focus", search: withHistoryState(search, true), replace: true })}>{t("focus.backToHistory")}</button>
                </div>
              ) : null}
              {thread.data !== undefined && selectedBranch === null ? (
                <div className={styles.inlineState} role="alert">
                  <strong>{t("focus.threadError")}</strong>
                  <button type="button" onClick={() => void thread.refetch()}>{t("focus.retryThread")}</button>
                </div>
              ) : null}
              {thread.data !== undefined && selectedBranch !== null && messages.isPending ? <p className={styles.inlineState} role="status">{t("focus.messagesLoading")}</p> : null}
              {messages.error !== null ? (
                <div className={styles.inlineState} role="alert">
                  <strong>{t("focus.messagesError")}</strong>
                  <button type="button" onClick={() => void messages.refetch()}>{t("focus.retryMessages")}</button>
                </div>
              ) : null}
              <div
                ref={messageScrollerRef}
                className={styles.messageScroller}
                aria-label={t("focus.threadDocument")}
                onScroll={(event) => {
                  const pinned = isPinnedToEnd(event.currentTarget);
                  if (pinned === pinnedRef.current) return;
                  pinnedRef.current = pinned;
                  setShowJumpLatest(!pinned);
                }}
              >
                {messages.hasPreviousPage ? <button type="button" className={styles.loadOlder} disabled={messages.isFetchingPreviousPage} onClick={() => void handleOlderMessages()}>{messages.isFetchingPreviousPage ? t("focus.loadingOlder") : t("focus.loadOlder")}</button> : null}
                {messageItems.length === 0 && !messages.isPending ? <p className={styles.inlineNotice}>{t("focus.threadEmpty")}</p> : null}
                {shouldShowPendingPrompt(pendingPrompt, threadId, selectedBranch?.id, messageItems) ? <PendingMessage prompt={pendingPrompt?.prompt ?? ""} /> : null}
                {messageItems.map((message) => <MessageDocument key={message.id} message={message} showActions={message.role === "assistant" && message.runId === runId && run !== null && isTerminalFocusState(run.state)} onCopy={handleCopy} />)}
                {runDetail.data?.queryCandidate?.state === "pending" && run?.state === "awaiting_query_review" ? <QueryReview candidate={runDetail.data.queryCandidate} pending={queryDecision.isPending} onDecision={(input) => void queryDecision.mutateAsync(input)} /> : null}
                {run !== null ? (
                  <RunEventPanel
                    key={run.id}
                    runId={run.id}
                    authoritativeSequence={run.currentEventSequence}
                    initialState={run.state}
                    showDelta={!hasPersistedAssistant}
                    cancelPending={cancelRun.isPending}
                    retryPending={retryRun.isPending}
                    retryAvailable={latestMessage !== null}
                    onBoundary={handleRunBoundary}
                    onTerminal={handleRunTerminal}
                    onContent={handleStreamContent}
                    onCancel={() => cancelRun.mutate()}
                    onRetry={() => void handleRetry()}
                  />
                ) : null}
                {proposalId !== null && (proposal.data === undefined || proposal.data.state === "pending") ? <ProposalReview proposal={proposal.data} pending={decideProposal.isPending} onDecision={(action) => decideProposal.mutate(action)} /> : null}
                {showJumpLatest ? <button type="button" className={styles.jumpLatest} onClick={followLatest}>{t("focus.jumpLatest")}</button> : null}
              </div>
              <Composer
                draft={draft}
                publicSearchEnabled={publicSearchEnabled}
                contextChoices={contextChoices}
                uploadEntries={uploadQueue.entries}
                onUploadFiles={(files) => uploadQueue.enqueue(files)}
                onRetryUpload={uploadQueue.retry}
                onCancelUpload={uploadQueue.cancel}
                onRemoveUpload={uploadQueue.remove}
                onOpenDocuments={() => void navigate({ to: "/documents", search: { workspace: workspaceId } })}
                contextLoading={contextDocuments.isPending || contextTasks.isPending}
                contextUnavailable={contextDocuments.isError || contextTasks.isError}
                contextRetrying={(contextDocuments.isError && contextDocuments.isFetching) || (contextTasks.isError && contextTasks.isFetching)}
                selectedContextKeys={selectedContextKeys}
                disabled={submitDisabled}
                pending={startRun.isPending}
                disabledReason={composerDisabledReason}
                onDraft={setDraft}
                onPublicSearchEnabled={setPublicSearchEnabled}
                onToggleContext={toggleContext}
                onRetryContext={retryContextSources}
                onSubmit={handleSubmit}
              />
            </>
          )}
          {mutationError !== null ? (
            <div className={styles.error} role="alert">
              <p>{partialStart !== null ? t("focus.partialStartFailed") : t("focus.requestFailed")}</p>
              {partialStart !== null ? <button type="button" disabled={startRun.isPending} onClick={() => void submitPrompt()}>{t("focus.retryStart")}</button> : null}
            </div>
          ) : null}
        </main>
      </div>

      <dialog
        ref={historyDialogRef}
        className={styles.historyDialog}
        aria-labelledby="focus-history-title"
        onCancel={(event) => {
          event.preventDefault();
          void navigate({ to: "/focus", search: withHistoryState(search, false), replace: true });
        }}
        onClose={handleHistoryDialogClose}
      >
        <header className={styles.drawerHeader}><h2 id="focus-history-title">{t("focus.history")}</h2><button type="button" onClick={() => void navigate({ to: "/focus", search: withHistoryState(search, false), replace: true })}>{t("focus.closeHistory")}</button></header>
        {historyPanel}
      </dialog>
    </section>
  );
}


type HistoryPanelProps = {
  readonly input: string; readonly query: string; readonly items: readonly FocusThreadProjection[]; readonly pending: boolean; readonly error: boolean; readonly hasMore: boolean;
  readonly onInput: (value: string) => void; readonly onSearch: () => void; readonly onClear: () => void; readonly onMore: () => void; readonly onRetry: () => void; readonly onOpen: (thread: FocusThreadProjection) => void | Promise<void>;
};

function HistoryPanel({ input, query, items, pending, error, hasMore, onInput, onSearch, onClear, onMore, onRetry, onOpen }: HistoryPanelProps) {
  const { t, i18n } = useTranslation();
  return (
    <div className={styles.historyPanel}>
      <div className={styles.historyPanelHeader}>
        <strong>{t("focus.history")}</strong>
        <span>{items.length}</span>
      </div>
      <form className={styles.historySearch} role="search" onSubmit={(event) => { event.preventDefault(); onSearch(); }}>
        <label>
          <span className={styles.visuallyHidden}>{t("focus.searchHistory")}</span>
          <input type="search" value={input} maxLength={500} placeholder={t("focus.searchHistoryPlaceholder")} onChange={(event) => onInput(event.currentTarget.value)} />
        </label>
        <div className={styles.compactActions}>
          <button type="submit" aria-label={t("focus.search")}><Search aria-hidden="true" size={17} /></button>
          {query.length > 0 ? <button type="button" onClick={onClear}>{t("focus.clearSearch")}</button> : null}
        </div>
      </form>
      <div className={styles.historyResults} aria-busy={pending}>
        {items.map((thread) => <button key={thread.id} type="button" className={styles.historyItem} onClick={() => void onOpen(thread)}><strong>{thread.title}</strong><time dateTime={thread.updatedAt}>{new Date(thread.updatedAt).toLocaleDateString(i18n.resolvedLanguage)}</time></button>)}
        {pending && items.length === 0 ? <p className={styles.inlineState} role="status">{t("focus.historyLoading")}</p> : null}
        {error ? <div className={styles.inlineState} role="alert"><p>{t("focus.historyError")}</p><button type="button" onClick={onRetry}>{t("common.retry")}</button></div> : null}
        {items.length === 0 && !pending && !error ? <p className={styles.inlineNotice}>{query.length > 0 ? t("focus.historyNoResults") : t("focus.historyEmpty")}</p> : null}
      </div>
      {hasMore && !error ? <button type="button" className={styles.loadMore} onClick={onMore}>{t("focus.moreHistory")}</button> : null}
    </div>
  );

}
function PendingMessage({ prompt }: { readonly prompt: string }) {
  const { t } = useTranslation();
  return (
    <article className={styles.userMessage} aria-busy="true">
      <p className={styles.messageLabel}>{t("focus.you")}</p>
      <p>{prompt}</p>
      <p className={styles.pendingTurn}>{t("focus.sending")}</p>
    </article>
  );
}

function MessageDocument({ message, showActions = false, onCopy }: { readonly message: FocusMessageProjection; readonly showActions?: boolean; readonly onCopy?: (text: string) => void | Promise<void> }) {
  const { t } = useTranslation();
  return (
    <article className={message.role === "assistant" ? styles.assistantMessage : styles.userMessage}>
      <p className={styles.messageLabel}>{message.role === "assistant" ? t("focus.assistant") : t("focus.you")}</p>
      <div className={styles.documentBody}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          skipHtml
          urlTransform={markdownUrlTransform}
          components={{
            a: ({ children, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer">{children}</a>,
            img: ({ alt }) => alt === undefined ? null : <span>{alt}</span>,
          }}
        >
          {message.bodyMarkdown}
        </ReactMarkdown>
      </div>
      {showActions && onCopy !== undefined ? (
        <div className={styles.messageActions}>
          <button type="button" onClick={() => void onCopy(message.bodyMarkdown)}><Copy aria-hidden="true" size={14} />{t("focus.copy")}</button>
        </div>
      ) : null}
    </article>
  );
}

function markdownUrlTransform(url: string): string {
  const normalized = url.trim();
  if (normalized.length === 0) return "";
  try {
    const parsed = new URL(normalized, window.location.origin);
    return parsed.protocol === "http:" || parsed.protocol === "https:" || parsed.protocol === "mailto:" ? normalized : "";
  } catch {
    return "";
  }
}
type ComposerProps = {
  readonly draft: string;
  readonly publicSearchEnabled: boolean;
  readonly contextChoices: readonly ContextChoice[];
  readonly uploadEntries: readonly DocumentUploadQueueEntry[];
  readonly onUploadFiles: (files: readonly File[]) => void;
  readonly onRetryUpload: (id: string) => void;
  readonly onCancelUpload: (id: string) => void;
  readonly onRemoveUpload: (id: string) => void;
  readonly onOpenDocuments: () => void;
  readonly contextLoading: boolean;
  readonly contextUnavailable: boolean;
  readonly contextRetrying: boolean;
  readonly selectedContextKeys: ReadonlySet<string>;
  readonly disabled: boolean;
  readonly pending: boolean;
  readonly disabledReason: string | null;
  readonly onDraft: (value: string) => void;
  readonly onPublicSearchEnabled: (enabled: boolean) => void;
  readonly onToggleContext: (key: string) => void;
  readonly onRetryContext: () => void;
  readonly onSubmit: (event: FormEvent<HTMLFormElement>) => void;
};

function Composer({
  draft,
  publicSearchEnabled,
  contextChoices,
  uploadEntries,
  onUploadFiles,
  onRetryUpload,
  onCancelUpload,
  onRemoveUpload,
  onOpenDocuments,
  contextLoading,
  contextUnavailable,
  contextRetrying,
  selectedContextKeys,
  disabled,
  disabledReason,
  pending,
  onDraft,
  onPublicSearchEnabled,
  onToggleContext,
  onRetryContext,
  onSubmit,
}: ComposerProps) {
  const { t, i18n } = useTranslation();
  const contextPanelId = useId();
  const contextButtonRef = useRef<HTMLButtonElement>(null);
  const [contextPickerOpen, setContextPickerOpen] = useState(false);
  const selectedChoices = contextChoices.filter((choice) => selectedContextKeys.has(choice.key));
  const contextButtonLabel = t("focus.context", { count: selectedContextKeys.size });

  const handleComposerKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (
      event.key === "Process"
      || event.key !== "Enter"
      || event.shiftKey
      || event.repeat
      || event.nativeEvent.isComposing
      || event.nativeEvent.keyCode === 229
    ) return;

    event.preventDefault();
    if (!disabled && !pending) event.currentTarget.form?.requestSubmit();
  };

  return (
    <form className={styles.composer} onSubmit={onSubmit}>
      {selectedChoices.length > 0 ? (
        <div className={styles.contextChips} aria-label={contextButtonLabel}>
          {selectedChoices.map((choice) => (
            <button
              key={choice.key}
              type="button"
              className={styles.contextChip}
              aria-pressed="true"
              aria-label={t("focus.removeContext", { label: choice.label })}
              onClick={() => onToggleContext(choice.key)}
            >
              <span>{choice.label}</span>
              <X aria-hidden="true" size={14} />
            </button>
          ))}
        </div>
      ) : null}
      {disabledReason !== null ? <p className={styles.composerNotice} role="status">{disabledReason}</p> : null}
      <label className={styles.composerField}>
        <span className={styles.visuallyHidden}>{t("focus.compose")}</span>
        <textarea
          value={draft}
          rows={2}
          maxLength={262_144}
          placeholder={t("focus.composePlaceholder")}
          onChange={(event) => onDraft(event.currentTarget.value)}
          onKeyDown={handleComposerKeyDown}
        />
      </label>
      <div className={styles.composerFooter}>
        <div className={styles.composerTools}>
          <div
            className={styles.contextPicker}
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget)) setContextPickerOpen(false);
            }}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                event.stopPropagation();
                setContextPickerOpen(false);
                contextButtonRef.current?.focus();
              }
            }}
          >
            <button
              ref={contextButtonRef}
              type="button"
              className={styles.toolButton}
              aria-label={contextButtonLabel}
              aria-controls={contextPanelId}
              aria-expanded={contextPickerOpen}
              onClick={() => setContextPickerOpen((current) => !current)}
            >
              <Plus aria-hidden="true" size={18} />
              <span>{contextButtonLabel}</span>
            </button>
            <div
              id={contextPanelId}
              className={styles.contextPanel}
              role="group"
              aria-label={contextButtonLabel}
              hidden={!contextPickerOpen}
            >
              <label className={styles.contextUpload}>
                <UploadCloud aria-hidden="true" size={17} />
                <span>{t("focus.uploadToDocuments")}</span>
                <input
                  className={styles.visuallyHidden}
                  type="file"
                  multiple
                  onChange={(event) => {
                    const files = event.currentTarget.files;
                    if (files !== null) onUploadFiles(Array.from(files));
                    event.currentTarget.value = "";
                  }}
                />
              </label>
              <UploadQueue entries={uploadEntries} locale={i18n.language} onRetry={onRetryUpload} onCancel={onCancelUpload} onRemove={onRemoveUpload} />
              <div className={styles.contextChoices}>
                {contextChoices.map((choice) => {
                  const selected = selectedContextKeys.has(choice.key);
                  return (
                    <label key={choice.key} className={styles.contextChoice} data-disabled={choice.disabledReason !== null ? "true" : "false"}>
                      <input
                        type="checkbox"
                        checked={selected}
                        disabled={!selected && (choice.disabledReason !== null || selectedContextKeys.size >= MAX_CONTEXT_CHOICES)}
                        onChange={() => onToggleContext(choice.key)}
                      />
                      <span><strong>{choice.label}</strong>{choice.disabledReason !== null ? <small>{choice.disabledReason}</small> : choice.detail === null ? null : <small>{choice.detail}</small>}</span>
                    </label>
                  );
                })}
                {contextUnavailable ? (
                  <div className={styles.contextUnavailable} role="alert">
                    <p>{t("focus.contextUnavailable")}</p>
                    <button type="button" disabled={contextRetrying} onClick={onRetryContext}>{t("common.retry")}</button>
                  </div>
                ) : contextChoices.length === 0 && contextLoading ? (
                  <p role="status">{t("focus.contextLoading")}</p>
                ) : contextChoices.length === 0 ? <p>{t("focus.contextEmpty")}</p> : null}
              </div>
              {contextChoices.some((choice) => choice.kind === "document" && choice.disabledReason !== null) ? <button type="button" className={styles.contextOpenDocuments} onClick={onOpenDocuments}>{t("focus.openDocuments")}</button> : null}
          </div>
          </div>
          <button
            type="button"
            className={styles.publicSearchButton}
            aria-pressed={publicSearchEnabled}
            onClick={() => onPublicSearchEnabled(!publicSearchEnabled)}
          >
            <Globe2 aria-hidden="true" size={18} />
            <span>{t("focus.publicSearch")}</span>
          </button>
        </div>
        {publicSearchEnabled ? <p className={styles.publicSearchDisclosure}>{t("focus.publicSearchDescription")}</p> : null}
        <button
          type="submit"
          className={styles.primaryButton}
          aria-label={pending ? t("focus.sending") : t("focus.send")}
          disabled={disabled}
        >
          <ArrowUp aria-hidden="true" size={18} />
        </button>
      </div>
    </form>
  );
}

function QueryReview({ candidate, pending, onDecision }: { readonly candidate: FocusQueryCandidate; readonly pending: boolean; readonly onDecision: (input: FocusQueryDecisionInput) => void }) {
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);
  return <form ref={formRef} className={styles.queryReview} aria-labelledby={`query-review-${candidate.runId}`}><h3 id={`query-review-${candidate.runId}`}>{t("focus.queryReview")}</h3><p>{t("focus.queryReviewDescription")}</p>{candidate.queries.map((query, index) => <label key={`${candidate.candidateRevision}:${index}`}><span>{t("focus.queryNumber", { number: index + 1 })}</span><input name="query" defaultValue={query} maxLength={2_048} /></label>)}<div className={styles.reviewActions}><button type="button" disabled={pending} onClick={() => onDecision({ decision: "approve" })}>{t("focus.approve")}</button><button type="button" disabled={pending} onClick={() => { const queries = formRef.current === null ? [] : new FormData(formRef.current).getAll("query").map((value) => String(value).trim()).filter(Boolean); onDecision({ decision: "edit", queries }); }}>{t("focus.saveQueries")}</button><button type="button" disabled={pending} onClick={() => onDecision({ decision: "reject" })}>{t("focus.reject")}</button></div></form>;
}


function ProposalReview({ proposal, pending, onDecision }: { readonly proposal: ProposalProjection | undefined; readonly pending: boolean; readonly onDecision: (action: "accept" | "reject") => void }) {
  const { t } = useTranslation();
  const operationKey = proposal === undefined ? "unknown" : getProposalOperationKey(proposal.command.type);
  const stateKey = proposal === undefined ? "unknown" : proposal.state;
  return (
    <details className={styles.proposalReview}>
      <summary>{t("focus.reviewProposal")}</summary>
      {proposal === undefined ? <p>{t("focus.proposalLoading")}</p> : (
        <div>
          <dl>
            <div><dt>{t("focus.proposalOperation")}</dt><dd>{t(`focus.proposalOperations.${operationKey}`)}</dd></div>
            <div><dt>{t("focus.proposalState")}</dt><dd>{t(`focus.proposalStates.${stateKey}`)}</dd></div>
          </dl>
          <p>{proposal.reason}</p>
          {proposal.state === "pending" ? (
            <div className={styles.reviewActions}>
              <button type="button" disabled={pending} onClick={() => onDecision("accept")}>{t("focus.acceptProposal")}</button>
              <button type="button" disabled={pending} onClick={() => onDecision("reject")}>{t("focus.rejectProposal")}</button>
            </div>
          ) : null}
        </div>
      )}
    </details>
  );
}
